import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  parseBoolean,
  ParsedExcelData,
  ValidationError
} from '@/lib/excel';
import { PhieuKeToanInput } from '@/types/schemas';

const HEADERS = {
  UNIT_CODE: 'Mã đơn vị',
  BOOK_NUMBER: 'Quyển/Số ct',
  DOC_NUMBER: 'Số c/từ*',
  DOC_DATE: 'Ngày chứng từ*',
  CURRENCY: 'Ngoại tệ*',
  EXCHANGE_RATE: 'Tỷ giá',
  DESCRIPTION: 'Diễn giải',
  ACCOUNT: 'Tài khoản*',
  ACCOUNT_NAME: 'Tên tài khoản',
  CUSTOMER_CODE: 'Mã khách hàng',
  CUSTOMER_NAME: 'Tên khách hàng',
  DEBIT: 'Ps nợ',
  CREDIT: 'Ps có',
  DEBIT_CONVERTED: 'Ps nợ quy đổi',
  CREDIT_CONVERTED: 'Ps có quy đổi',
  GROUP: 'Nhóm',
  DETAIL_DESC: 'Diễn giải',
  INVOICE_NUMBER: 'Số hóa đơn',
  INVOICE_DATE: 'Ngày hóa đơn',
  DEPARTMENT: 'Bộ phận',
  FEE: 'Phí',
  PROJECT: 'Vụ việc',
  CONTRACT: 'Hợp đồng',
  PAYMENT_TERM: 'Đợt thanh toán',
  LOAN: 'Khế ước',
  PRODUCT: 'Sản phẩm',
  PRODUCTION_ORDER: 'Lệnh sản xuất',
  TAX_CODE: 'Mã thuế',
  TAX_INVOICE_NUMBER: 'Số hóa đơn',
  TAX_SYMBOL: 'Ký hiệu',
  TAX_INVOICE_DATE: 'Ngày hóa đơn',
  INVOICE_TEMPLATE: 'Mẫu hóa đơn',
  REPORT_TEMPLATE: 'Mẫu báo cáo',
  TAX_TYPE: 'Mã tính chất',
  SUPPLIER_CODE: 'Mã ncc',
  SUPPLIER_NAME: 'Tên nhà cung cấp',
  TAX_ADDRESS: 'Địa chỉ',
  TAX_ID: 'Mã số thuế',
  GOODS_NAME: 'Tên hàng hóa - dịch vụ',
  GOODS_AMOUNT: 'Tiền hàng',
  GOODS_AMOUNT_CONVERTED: 'Tiền hàng quy đổi',
  TAX_ACCOUNT: 'Tk thuế',
  OFFSET_ACCOUNT: 'Tk đối ứng',
  TAX_AMOUNT: 'Thuế',
  TAX_AMOUNT_CONVERTED: 'Thuế quy đổi',
  TAX_AUTHORITY: 'Cục thuế',
  PAYMENT_CODE: 'Mã thanh toán',
  NOTE: 'Ghi chú',
  TAX_DEPARTMENT: 'Bộ phận',
  TAX_PROJECT: 'Vụ việc',
  TAX_CONTRACT: 'Hợp đồng',
  TAX_PAYMENT_TERM: 'Đợt thanh toán',
  TAX_LOAN: 'Khế ước',
  TAX_FEE: 'Phí',
  TAX_PRODUCT: 'Sản phẩm',
  TAX_PRODUCTION_ORDER: 'Lệnh sản xuất'
};

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.UNIT_CODE, key: 'unit_id', type: 'main' },
  { header: HEADERS.BOOK_NUMBER, key: 'i_so_ct', type: 'main' },
  { header: HEADERS.DOC_NUMBER, key: 'so_ct', type: 'main' },
  { header: HEADERS.DOC_DATE, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.CURRENCY, key: 'ma_nt', type: 'main' },
  { header: HEADERS.EXCHANGE_RATE, key: 'ty_gia', type: 'main' },
  { header: HEADERS.DESCRIPTION, key: 'dien_giai', type: 'main' },
  { header: HEADERS.ACCOUNT, key: 'tk', type: 'detail' },
  { header: HEADERS.CUSTOMER_CODE, key: 'ma_kh', type: 'detail' },
  { header: HEADERS.DEBIT, key: 'ps_no_nt', type: 'detail' },
  { header: HEADERS.CREDIT, key: 'ps_co_nt', type: 'detail' },
  { header: HEADERS.DEBIT_CONVERTED, key: 'ps_no', type: 'detail' },
  { header: HEADERS.CREDIT_CONVERTED, key: 'ps_co', type: 'detail' },
  { header: HEADERS.GROUP, key: 'nh_dk', type: 'detail' },
  { header: HEADERS.DETAIL_DESC, key: 'dien_giai', type: 'detail' },
  { header: HEADERS.INVOICE_NUMBER, key: 'so_ct0', type: 'detail' },
  { header: HEADERS.INVOICE_DATE, key: 'ngay_ct0', type: 'detail' },
  { header: HEADERS.DEPARTMENT, key: 'ma_bp', type: 'detail' },
  { header: HEADERS.FEE, key: 'ma_phi', type: 'detail' },
  { header: HEADERS.PROJECT, key: 'ma_vv', type: 'detail' },
  { header: HEADERS.CONTRACT, key: 'ma_hd', type: 'detail' },
  { header: HEADERS.PAYMENT_TERM, key: 'ma_dtt', type: 'detail' },
  { header: HEADERS.LOAN, key: 'ma_ku', type: 'detail' },
  { header: HEADERS.PRODUCT, key: 'ma_sp', type: 'detail' },
  { header: HEADERS.PRODUCTION_ORDER, key: 'ma_lsx', type: 'detail' },
  { header: HEADERS.TAX_CODE, key: 'ma_thue', type: 'tax' },
  { header: HEADERS.TAX_INVOICE_NUMBER, key: 'so_ct0', type: 'tax' },
  { header: HEADERS.TAX_SYMBOL, key: 'so_ct2', type: 'tax' },
  { header: HEADERS.TAX_INVOICE_DATE, key: 'ngay_ct0', type: 'tax' },
  { header: HEADERS.INVOICE_TEMPLATE, key: 'ma_mau_ct', type: 'tax' },
  { header: HEADERS.REPORT_TEMPLATE, key: 'ma_mau_bc', type: 'tax' },
  { header: HEADERS.TAX_TYPE, key: 'ma_tc_thue', type: 'tax' },
  { header: HEADERS.SUPPLIER_CODE, key: 'ma_kh', type: 'tax' },
  { header: HEADERS.GOODS_NAME, key: 'ten_vt_thue', type: 'tax' },
  { header: HEADERS.GOODS_AMOUNT, key: 't_tien_nt', type: 'tax' },
  { header: HEADERS.GOODS_AMOUNT_CONVERTED, key: 't_tien', type: 'tax' },
  { header: HEADERS.TAX_ACCOUNT, key: 'tk_thue_no', type: 'tax' },
  { header: HEADERS.OFFSET_ACCOUNT, key: 'tk_du', type: 'tax' },
  { header: HEADERS.TAX_AMOUNT, key: 't_thue_nt', type: 'tax' },
  { header: HEADERS.TAX_AMOUNT_CONVERTED, key: 't_thue', type: 'tax' },
  { header: HEADERS.TAX_AUTHORITY, key: 'ma_kh9', type: 'tax' },
  { header: HEADERS.PAYMENT_CODE, key: 'ma_tt', type: 'tax' },
  { header: HEADERS.NOTE, key: 'ghi_chu', type: 'tax' },
  { header: HEADERS.TAX_DEPARTMENT, key: 'ma_bp', type: 'tax' },
  { header: HEADERS.TAX_PROJECT, key: 'ma_vv', type: 'tax' },
  { header: HEADERS.TAX_CONTRACT, key: 'ma_hd', type: 'tax' },
  { header: HEADERS.TAX_PAYMENT_TERM, key: 'ma_dtt', type: 'tax' },
  { header: HEADERS.TAX_LOAN, key: 'ma_ku', type: 'tax' },
  { header: HEADERS.TAX_FEE, key: 'ma_phi', type: 'tax' },
  { header: HEADERS.TAX_PRODUCT, key: 'ma_sp', type: 'tax' },
  { header: HEADERS.TAX_PRODUCTION_ORDER, key: 'ma_lsx', type: 'tax' }
];

const UUID_FIELDS = {
  main: ['ma_nt'],
  detail: ['tk', 'ma_kh', 'ma_bp', 'ma_vv', 'ma_hd', 'ma_dtt', 'ma_ku', 'ma_phi', 'ma_sp', 'ma_lsx', 'ma_cp0'],
  tax: [
    'ma_thue',
    'ma_mau_ct',
    'ma_tc_thue',
    'ma_kh',
    'tk_thue_no',
    'tk_du',
    'ma_kh9',
    'ma_tt',
    'ma_bp',
    'ma_vv',
    'ma_hd',
    'ma_dtt',
    'ma_ku',
    'ma_phi',
    'ma_sp',
    'ma_lsx'
  ]
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const parseNum = (val: any, def = 0) => {
  if (val == null || String(val).trim() === '') return def;
  const n = parseFloat(String(val));
  return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
};

const hasData = (item: Record<string, any>, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

const getVal = (uuid: any, parsed: any, section: string, field: string, i: number) =>
  uuid?.[section]?.[field]?.[i] || parsed[field]?.[i] || undefined;

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: Record<string, any[]>, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section[f] || []]));

  return {
    main: build(data.main || {}, UUID_FIELDS.main),
    chi_tiet: build(data.detail || {}, UUID_FIELDS.detail),
    thue: build(data.tax || {}, UUID_FIELDS.tax)
  };
};

const ValidateUUID = (response: any, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main', 'chi_tiet', 'thue'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: any[][],
  parsedData: ParsedExcelData,
  uuidResponse: any,
  context: { entityUnitUUID: string }
): ImportBody[] => {
  const { main = {}, detail = {}, tax = {} } = parsedData;
  const records = new Map<string, ImportBody>();
  const detailMap = new Map<string, ImportBody['chi_tiet']>();
  const taxMap = new Map<string, ImportBody['thue']>();

  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'detail').map(c => c.key);
  const taxKeys = HEADER_CONFIG.filter(c => c.type === 'tax').map(c => c.key);

  for (let i = 0; i < dataRows.length; i++) {
    const docNum = String(main.so_ct?.[i] ?? '').trim();
    if (!docNum) continue;

    if (!records.has(docNum)) {
      const docDate = convertExcelDate(main.ngay_ct?.[i]) || format(new Date(), 'yyyy-MM-dd');
      records.set(docNum, {
        unit_id: context.entityUnitUUID,
        ma_ngv: '2',
        dien_giai: main.dien_giai?.[i] || undefined,
        so_ct: docNum,
        ngay_ct: docDate,
        ngay_lct: docDate,
        i_so_ct: 0,
        ma_nt: getVal(uuidResponse, main, 'main', 'ma_nt', i) || '',
        ty_gia: parseNum(main.ty_gia?.[i], 1),
        status: '2',
        transfer_yn: parseBoolean(main.transfer_yn?.[i]),
        t_ps_no_nt: 0,
        t_ps_co_nt: 0,
        chi_tiet: [],
        thue: []
      });
    }

    const details = detailMap.get(docNum) || [];
    const detailItem = {
      line: details.length + 1,
      tk: getVal(uuidResponse, detail, 'chi_tiet', 'tk', i),
      ma_kh: getVal(uuidResponse, detail, 'chi_tiet', 'ma_kh', i),
      ps_no_nt: parseNum(detail.ps_no_nt?.[i]),
      ps_co_nt: parseNum(detail.ps_co_nt?.[i]),
      nh_dk: parseNum(detail.nh_dk?.[i]) || undefined,
      dien_giai: detail.dien_giai?.[i] || main.dien_giai?.[i] || undefined,
      so_ct0: detail.so_ct0?.[i] || undefined,
      ngay_ct0: detail.ngay_ct0?.[i] ? convertExcelDate(detail.ngay_ct0[i]) : undefined,
      ma_bp: getVal(uuidResponse, detail, 'chi_tiet', 'ma_bp', i),
      ma_vv: getVal(uuidResponse, detail, 'chi_tiet', 'ma_vv', i),
      ma_hd: getVal(uuidResponse, detail, 'chi_tiet', 'ma_hd', i),
      ma_dtt: getVal(uuidResponse, detail, 'chi_tiet', 'ma_dtt', i),
      ma_ku: getVal(uuidResponse, detail, 'chi_tiet', 'ma_ku', i),
      ma_phi: getVal(uuidResponse, detail, 'chi_tiet', 'ma_phi', i),
      ma_sp: getVal(uuidResponse, detail, 'chi_tiet', 'ma_sp', i),
      ma_lsx: getVal(uuidResponse, detail, 'chi_tiet', 'ma_lsx', i),
      ma_cp0: getVal(uuidResponse, detail, 'chi_tiet', 'ma_cp0', i)
    };

    if (hasData(detailItem, detailKeys)) {
      details.push(detailItem);
      detailMap.set(docNum, details);
    }

    const taxes = taxMap.get(docNum) || [];
    const taxItem = {
      line: taxes.length + 1,
      so_ct0: tax.so_ct0?.[i] || undefined,
      so_ct2: tax.so_ct2?.[i] || undefined,
      ngay_ct0: tax.ngay_ct0?.[i] ? convertExcelDate(tax.ngay_ct0[i]) : undefined,
      ma_thue: getVal(uuidResponse, tax, 'thue', 'ma_thue', i),
      ma_mau_ct: getVal(uuidResponse, tax, 'thue', 'ma_mau_ct', i),
      ma_mau_bc: tax.ma_mau_bc?.[i] || undefined,
      ma_tc_thue: getVal(uuidResponse, tax, 'thue', 'ma_tc_thue', i),
      ma_kh: getVal(uuidResponse, tax, 'thue', 'ma_kh', i),
      ten_vt_thue: tax.ten_vt_thue?.[i] || undefined,
      t_tien_nt: parseNum(tax.t_tien_nt?.[i]),
      t_tien: parseNum(tax.t_tien?.[i]) || parseNum(tax.t_tien_nt?.[i]),
      tk_thue_no: getVal(uuidResponse, tax, 'thue', 'tk_thue_no', i),
      tk_du: getVal(uuidResponse, tax, 'thue', 'tk_du', i),
      t_thue_nt: parseNum(tax.t_thue_nt?.[i]),
      t_thue: parseNum(tax.t_thue?.[i]) || parseNum(tax.t_thue_nt?.[i]),
      ma_kh9: getVal(uuidResponse, tax, 'thue', 'ma_kh9', i),
      ma_tt: getVal(uuidResponse, tax, 'thue', 'ma_tt', i),
      ghi_chu: tax.ghi_chu?.[i] || undefined,
      ma_bp: getVal(uuidResponse, tax, 'thue', 'ma_bp', i),
      ma_vv: getVal(uuidResponse, tax, 'thue', 'ma_vv', i),
      ma_hd: getVal(uuidResponse, tax, 'thue', 'ma_hd', i),
      ma_dtt: getVal(uuidResponse, tax, 'thue', 'ma_dtt', i),
      ma_ku: getVal(uuidResponse, tax, 'thue', 'ma_ku', i),
      ma_phi: getVal(uuidResponse, tax, 'thue', 'ma_phi', i),
      ma_sp: getVal(uuidResponse, tax, 'thue', 'ma_sp', i),
      ma_lsx: getVal(uuidResponse, tax, 'thue', 'ma_lsx', i)
    };

    if (hasData(taxItem, taxKeys)) {
      taxes.push(taxItem);
      taxMap.set(docNum, taxes);
    }
  }

  return Array.from(records.values()).map(record => {
    record.chi_tiet = detailMap.get(record.so_ct || '') || [];
    record.thue = taxMap.get(record.so_ct || '') || [];
    record.t_ps_no_nt = Math.round(record.chi_tiet.reduce((a, i) => a + (i.ps_no_nt || 0), 0) * 100) / 100;
    record.t_ps_co_nt = Math.round(record.chi_tiet.reduce((a, i) => a + (i.ps_co_nt || 0), 0) * 100) / 100;
    return record;
  });
};

export interface ImportBody extends Omit<PhieuKeToanInput, 'chi_tiet' | 'thue'> {
  chi_tiet?: Array<{
    line?: number;
    tk?: string;
    ma_kh?: string | null;
    ma_vv?: string | null;
    ma_phi?: string | null;
    ma_sp?: string | null;
    ma_cp0?: string | null;
    ma_bp?: string | null;
    ma_hd?: string | null;
    ma_dtt?: string | null;
    ma_ku?: string | null;
    ma_lsx?: string | null;
    dien_giai?: string | null;
    ps_no_nt?: number;
    ps_co_nt?: number;
    nh_dk?: number;
    so_ct0?: string | null;
    ngay_ct0?: string | null;
  }>;
  thue?: Array<{
    line?: number;
    so_ct0?: string | null;
    so_ct2?: string | null;
    ngay_ct0?: string | null;
    ma_thue?: string | null;
    ma_mau_ct?: string | null;
    ma_mau_bc?: string | null;
    ma_tc_thue?: string | null;
    ma_kh?: string | null;
    ten_vt_thue?: string | null;
    t_tien_nt?: number;
    t_tien?: number;
    tk_thue_no?: string | null;
    tk_du?: string | null;
    t_thue_nt?: number;
    t_thue?: number;
    ma_kh9?: string | null;
    ma_tt?: string | null;
    ghi_chu?: string | null;
    ma_bp?: string | null;
    ma_vv?: string | null;
    ma_hd?: string | null;
    ma_dtt?: string | null;
    ma_ku?: string | null;
    ma_phi?: string | null;
    ma_sp?: string | null;
    ma_lsx?: string | null;
    ma_cp0?: string | null;
  }>;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
